import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, MessageSquare, Send, ShieldCheck } from 'lucide-react';
import {
  SMS_BODY_FORMATS,
  SMS_METHODS,
  SMS_SUCCESS_TESTS,
  type SmsMessageRecord,
  type SmsProviderId,
  type SmsRequestTemplate,
  type SmsSendOutcome,
  type SmsSettingsView,
} from '@simpleacs/shared';
import { api } from '../../lib/api';
import { formatRelative } from '../../lib/format';
import {
  Button,
  Card,
  ErrorNotice,
  Field,
  Input,
  Pill,
  Table,
  TBody,
  THead,
  TRow,
} from '../../components/ui';

/**
 * The SMS gateway, as the operator asked for it: pick one of the four named
 * providers, or fill in the boxes yourself.
 *
 * The layout is the argument for the design. The provider picker and the
 * credential fields are the whole job for a listed provider — choose Twilio,
 * paste three values, press Send test. Everything below that is the SAME
 * request, shown; the presets are not hidden behind a special case, they are
 * these very fields pre-filled. An operator on an unlisted provider is doing
 * exactly what the Twilio operator's configuration does, which is what makes
 * the "paste your own" path worth trusting.
 *
 * What the page does NOT offer is a place to write code. The operator asked to
 * paste headers and a body, and that is what these boxes take — a fixed list of
 * substitutions, no expressions. That decision is stated on the page rather
 * than hidden, because somebody will eventually want to compute an HMAC here
 * and deserves to know why they cannot.
 */
export function SmsTab() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['sms', 'settings'],
    queryFn: () => api.get<SmsSettingsView>('/sms/settings'),
  });
  const messages = useQuery({
    queryKey: ['sms', 'messages'],
    queryFn: () => api.get<{ items: SmsMessageRecord[] }>('/sms/messages', { limit: 20 }),
  });

  const [template, setTemplate] = useState<SmsRequestTemplate | null>(null);
  const [creds, setCreds] = useState<Record<string, string>>({});
  const [secrets, setSecrets] = useState<{ apiKey: string; apiSecret: string }>({
    apiKey: '',
    apiSecret: '',
  });
  const [testTo, setTestTo] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<SmsSendOutcome | null>(null);

  const seeded = useRef(false);
  useEffect(() => {
    if (!query.data || seeded.current) return;
    seeded.current = true;
    setTemplate(query.data.template);
    setCreds({
      senderId: query.data.senderId,
      username: query.data.username,
      accountId: query.data.accountId,
    });
  }, [query.data]);

  const view = query.data;
  const preset = view?.presets.find((p) => p.id === view.provider);

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.put<SmsSettingsView>('/sms/settings', body),
    onSuccess: async (next) => {
      setError(null);
      setNotice('Saved.');
      setTimeout(() => setNotice(null), 4000);
      setTemplate(next.template);
      setSecrets({ apiKey: '', apiSecret: '' });
      queryClient.setQueryData(['sms', 'settings'], next);
      await queryClient.invalidateQueries({ queryKey: ['sms'] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not save'),
  });

  const test = useMutation({
    mutationFn: () => api.post<SmsSendOutcome>('/sms/test', { to: testTo }),
    onSuccess: async (r) => {
      setOutcome(r);
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['sms', 'messages'] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not send'),
  });

  if (query.isError) return <ErrorNotice error={query.error} />;
  if (!view || !template) return null;

  const applyPreset = (id: SmsProviderId) =>
    save.mutate({ applyPreset: id, ...credentialBody(creds, secrets) });

  const saveAll = (extra: Record<string, unknown> = {}) =>
    save.mutate({ template, ...credentialBody(creds, secrets), ...extra });

  const patch = (change: Partial<SmsRequestTemplate>) => setTemplate({ ...template, ...change });

  return (
    <div className="space-y-5">
      <Card
        title={
          <span className="flex items-center gap-2">
            <MessageSquare className="size-4 text-muted" />
            SMS gateway
          </span>
        }
        action={
          <Pill state tone={view.enabled ? (view.configured ? 'success' : 'warn') : 'neutral'}>
            {view.enabled ? (view.configured ? 'On' : 'On, incomplete') : 'Off'}
          </Pill>
        }
      >
        <div className="space-y-4 p-5">
          <p className="text-sm text-muted">
            Choose a provider and fill in its credentials. Anything not listed works too — the
            listed providers are these same boxes, pre-filled.
          </p>

          <div className="flex flex-wrap gap-2">
            {view.presets.map((p) => (
              <button
                key={p.id}
                onClick={() => applyPreset(p.id)}
                disabled={save.isPending}
                className={
                  p.id === view.provider
                    ? 'rounded-lg border border-primary bg-primary/10 px-3 py-1.5 text-sm font-medium text-primary'
                    : 'rounded-lg border border-line px-3 py-1.5 text-sm text-muted hover:text-ink'
                }
              >
                {p.name}
              </button>
            ))}
          </div>

          {preset ? (
            <div className="rounded-lg border border-line p-3 text-sm">
              <div className="mb-1 flex items-center gap-2">
                {preset.confidence === 'verified' ? (
                  <ShieldCheck className="size-4 text-success" />
                ) : (
                  <AlertTriangle className="size-4 text-warn" />
                )}
                <span className="font-medium text-ink">
                  {preset.confidence === 'verified'
                    ? 'Request shape taken from the provider’s published API.'
                    : 'Not confirmed against a live account — check it before you rely on it.'}
                </span>
              </div>
              <p className="text-muted">{preset.note}</p>
              {preset.docsUrl ? (
                <a
                  className="text-primary hover:underline"
                  href={preset.docsUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Provider documentation
                </a>
              ) : null}
            </div>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            {(preset?.fields ?? []).map((f) => (
              <Field
                key={f.key}
                label={`${f.label}${f.required ? '' : ' (optional)'}`}
                hint={
                  f.secret
                    ? storedHint(view, f.key) || f.hint
                    : f.hint
                }
              >
                <Input
                  type={f.secret ? 'password' : 'text'}
                  autoComplete="off"
                  placeholder={f.secret && storedHint(view, f.key) ? 'Leave blank to keep' : ''}
                  value={
                    f.secret
                      ? (secrets[f.key as 'apiKey' | 'apiSecret'] ?? '')
                      : (creds[f.key] ?? '')
                  }
                  onChange={(e) =>
                    f.secret
                      ? setSecrets({ ...secrets, [f.key]: e.target.value })
                      : setCreds({ ...creds, [f.key]: e.target.value })
                  }
                />
              </Field>
            ))}
          </div>

          {view.problems.length > 0 ? (
            <ul className="rounded-lg border border-warn/40 bg-warn/5 p-3 text-sm text-muted">
              {view.problems.map((p) => (
                <li key={p}>• {p}</li>
              ))}
            </ul>
          ) : null}

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="primary" loading={save.isPending} onClick={() => saveAll()}>
              Save
            </Button>
            <Button
              variant="secondary"
              disabled={!view.configured}
              loading={save.isPending}
              onClick={() => saveAll({ enabled: !view.enabled })}
            >
              {view.enabled ? 'Switch off' : 'Switch on'}
            </Button>
            <span className="text-xs text-faint">
              Switching on is refused while anything the provider needs is missing.
            </span>
          </div>
        </div>
      </Card>

      <Card title="Send a test message">
        <div className="space-y-3 p-5">
          <p className="text-sm text-muted">
            Sends one real message and shows exactly what the gateway answered. Works while the
            gateway is switched off — configure, test, then switch on.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-64">
              <Field label="To" hint="International format, e.g. +254712345678">
                <Input
                  value={testTo}
                  onChange={(e) => setTestTo(e.target.value)}
                  placeholder="+254712345678"
                />
              </Field>
            </div>
            <Button
              icon={<Send className="size-3.5" />}
              loading={test.isPending}
              disabled={testTo.trim() === ''}
              onClick={() => test.mutate()}
            >
              Send test
            </Button>
          </div>
          {outcome ? (
            <div
              className={
                outcome.ok
                  ? 'rounded-lg border border-success/40 bg-success/5 p-3 text-sm'
                  : 'rounded-lg border border-danger/40 bg-danger/5 p-3 text-sm'
              }
            >
              <p className="text-ink">{outcome.detail}</p>
              <p className="mt-1 text-xs text-faint">
                {outcome.httpStatus !== null ? `HTTP ${outcome.httpStatus} · ` : ''}
                {outcome.latencyMs} ms
                {outcome.providerMessageId ? ` · id ${outcome.providerMessageId}` : ''}
              </p>
              {outcome.responseSnippet ? (
                <pre className="mt-2 max-h-40 overflow-auto rounded bg-black/5 p-2 text-xs">
                  {outcome.responseSnippet}
                </pre>
              ) : null}
            </div>
          ) : null}
        </div>
      </Card>

      <Card title="The request">
        <div className="space-y-4 p-5">
          <p className="text-sm text-muted">
            This is what gets sent. The listed providers fill it in for you; edit it for anything
            else. Values are substituted where you write{' '}
            {view.placeholders.map((p) => (
              <code key={p} className="mx-0.5 rounded bg-black/5 px-1 text-xs">{`{{${p}}}`}</code>
            ))}
            . Nothing here is executed — there are no expressions, only these names, which is why a
            pasted configuration cannot become code running on this server. Put credentials in the
            fields above and reference them, rather than pasting a key into a header.
          </p>

          <div className="grid gap-3 sm:grid-cols-[8rem_1fr]">
            <Field label="Method">
              <select
                className="input"
                value={template.method}
                onChange={(e) => patch({ method: e.target.value as SmsRequestTemplate['method'] })}
              >
                {SMS_METHODS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="URL" hint="Must be a public https address — this server will not call its own network.">
              <Input value={template.url} onChange={(e) => patch({ url: e.target.value })} />
            </Field>
          </div>

          <div>
            <span className="label">Headers</span>
            <div className="space-y-2">
              {template.headers.map((h, i) => (
                <div key={i} className="flex gap-2">
                  <Input
                    className="w-56"
                    value={h.name}
                    placeholder="Header"
                    onChange={(e) =>
                      patch({
                        headers: template.headers.map((x, j) =>
                          j === i ? { ...x, name: e.target.value } : x,
                        ),
                      })
                    }
                  />
                  <Input
                    value={h.value}
                    placeholder="Value"
                    onChange={(e) =>
                      patch({
                        headers: template.headers.map((x, j) =>
                          j === i ? { ...x, value: e.target.value } : x,
                        ),
                      })
                    }
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => patch({ headers: template.headers.filter((_, j) => j !== i) })}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => patch({ headers: [...template.headers, { name: '', value: '' }] })}
              >
                Add header
              </Button>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Basic auth" hint="Some providers (Twilio) authenticate this way.">
              <select
                className="input"
                value={template.auth.mode}
                onChange={(e) =>
                  patch({
                    auth:
                      e.target.value === 'basic'
                        ? { mode: 'basic', username: '{{accountId}}', password: '{{apiSecret}}' }
                        : { mode: 'none' },
                  })
                }
              >
                <option value="none">Not used</option>
                <option value="basic">HTTP Basic</option>
              </select>
            </Field>
            {template.auth.mode === 'basic' ? (
              <>
                <Field label="Basic username">
                  <Input
                    value={template.auth.username}
                    onChange={(e) =>
                      patch({
                        auth: {
                          mode: 'basic',
                          username: e.target.value,
                          password: template.auth.mode === 'basic' ? template.auth.password : '',
                        },
                      })
                    }
                  />
                </Field>
                <Field label="Basic password">
                  <Input
                    value={template.auth.password}
                    onChange={(e) =>
                      patch({
                        auth: {
                          mode: 'basic',
                          username: template.auth.mode === 'basic' ? template.auth.username : '',
                          password: e.target.value,
                        },
                      })
                    }
                  />
                </Field>
              </>
            ) : null}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label="Body format"
              hint="Also decides how values are escaped — JSON quotes, form percent-encoding."
            >
              <select
                className="input"
                value={template.bodyFormat}
                onChange={(e) =>
                  patch({ bodyFormat: e.target.value as SmsRequestTemplate['bodyFormat'] })
                }
              >
                {SMS_BODY_FORMATS.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Content type" hint="Leave blank to use the format’s own.">
              <Input
                value={template.contentType}
                placeholder="application/json"
                onChange={(e) => patch({ contentType: e.target.value })}
              />
            </Field>
          </div>

          <Field label="Body">
            <textarea
              className="input h-28 font-mono text-xs"
              value={template.body}
              onChange={(e) => patch({ body: e.target.value })}
            />
          </Field>

          <div className="rounded-lg border border-line p-3">
            <p className="mb-3 text-sm text-muted">
              <strong className="text-ink">How the answer is read.</strong> Every one of these
              gateways can refuse a message with HTTP 200, so “sent” means a field in the reply says
              so — not that the call went through.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Field in the reply" hint="e.g. SMSMessageData.Recipients[0].status">
                <Input
                  value={template.success.path}
                  onChange={(e) =>
                    patch({ success: { ...template.success, path: e.target.value } })
                  }
                />
              </Field>
              <Field label="Test">
                <select
                  className="input"
                  value={template.success.test}
                  onChange={(e) =>
                    patch({
                      success: {
                        ...template.success,
                        test: e.target.value as SmsRequestTemplate['success']['test'],
                      },
                    })
                  }
                >
                  {SMS_SUCCESS_TESTS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Accepted values" hint="Comma separated. Compared case-insensitively.">
                <Input
                  value={template.success.values.join(', ')}
                  onChange={(e) =>
                    patch({
                      success: {
                        ...template.success,
                        values: e.target.value
                          .split(',')
                          .map((v) => v.trim())
                          .filter((v) => v !== ''),
                      },
                    })
                  }
                />
              </Field>
              <Field label="Message id field" hint="Kept in the record, so a send can be traced.">
                <Input
                  value={template.success.idPath}
                  onChange={(e) =>
                    patch({ success: { ...template.success, idPath: e.target.value } })
                  }
                />
              </Field>
              <Field label="Error field" hint="Where the gateway explains a refusal.">
                <Input
                  value={template.success.errorPath}
                  onChange={(e) =>
                    patch({ success: { ...template.success, errorPath: e.target.value } })
                  }
                />
              </Field>
            </div>
            {view.statusOnly ? (
              <p className="mt-3 text-xs text-warn">
                No reply field is checked, so a send counts as successful whenever the status code is
                in range. That is acceptance by the network, not by the gateway.
              </p>
            ) : null}
          </div>

          <div className="flex items-center gap-3">
            <Button variant="primary" loading={save.isPending} onClick={() => saveAll()}>
              Save
            </Button>
            {notice ? <span className="text-sm text-success">{notice}</span> : null}
            {error ? <span className="text-sm text-danger">{error}</span> : null}
          </div>
        </div>
      </Card>

      <Card title="What has been sent">
        <div className="overflow-x-auto">
          <Table>
            <THead>
              <tr>
                <th className="th">When</th>
                <th className="th">To</th>
                <th className="th">Outcome</th>
                <th className="th">Message</th>
              </tr>
            </THead>
            <TBody>
              {(messages.data?.items ?? []).map((m) => (
                <TRow key={m.id}>
                  <td className="td whitespace-nowrap">{formatRelative(m.at)}</td>
                  <td className="td whitespace-nowrap">{m.to}</td>
                  <td className="td">
                    <span className="flex items-center gap-2">
                      <Pill state tone={m.ok ? 'success' : 'danger'}>{m.ok ? 'Sent' : 'Failed'}</Pill>
                      <span className="text-xs text-muted">{m.detail}</span>
                    </span>
                  </td>
                  <td className="td text-xs text-muted">{m.message}</td>
                </TRow>
              ))}
            </TBody>
          </Table>
          {(messages.data?.items ?? []).length === 0 ? (
            <p className="p-5 text-sm text-muted">Nothing has been sent yet.</p>
          ) : null}
        </div>
      </Card>
    </div>
  );
}

/**
 * An empty secret field means "leave it alone", never "delete it".
 *
 * The form renders those boxes blank on purpose — the server does not send
 * the values back — so submitting them as-is would wipe a working credential
 * on every unrelated save.
 */
function credentialBody(
  creds: Record<string, string>,
  secrets: { apiKey: string; apiSecret: string },
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    senderId: creds.senderId ?? '',
    username: creds.username ?? '',
    accountId: creds.accountId ?? '',
  };
  if (secrets.apiKey !== '') body.apiKey = secrets.apiKey;
  if (secrets.apiSecret !== '') body.apiSecret = secrets.apiSecret;
  return body;
}

function storedHint(view: SmsSettingsView, key: string): string {
  if (key === 'apiKey' && view.apiKeySet) return `Stored: ${view.apiKeyHint}`;
  if (key === 'apiSecret' && view.apiSecretSet) return `Stored: ${view.apiSecretHint}`;
  return '';
}
